import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      setLoading(false)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null)
        setLoading(false)
      }
    )

    return () => subscription?.unsubscribe()
  }, [])

  const signUp = async (email, password, userData = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData
      }
    })

    if (data.user && !error) {
      // Create user profile
      await supabase
        .from('profiles')
        .insert({
          user_id: data.user.id,
          email: data.user.email,
          full_name: userData.full_name || '',
          subscription_tier: 'free'
        })

      // Create initial portfolio
      await supabase
        .from('portfolios')
        .insert({
          user_id: data.user.id,
          total_value: 0,
          available_balance: 0,
          invested_amount: 0
        })

      // Create default risk settings
      await supabase
        .from('risk_settings')
        .insert({
          user_id: data.user.id,
          max_position_size: 10.0,
          max_daily_loss: 5.0,
          stop_loss_percentage: 2.0,
          take_profit_percentage: 5.0,
          risk_tolerance: 'medium'
        })
    }

    return { data, error }
  }

  const signIn = async (email, password) => {
    return await supabase.auth.signInWithPassword({ email, password })
  }

  const signOut = async () => {
    return await supabase.auth.signOut()
  }

  const resetPassword = async (email) => {
    return await supabase.auth.resetPasswordForEmail(email)
  }

  const value = {
    user,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}